import React, {useState} from "react";
import CustomDialog from "../../../core/components/ui/CustomDialog";
import {useTheme, Box, CircularProgress} from "@mui/material";
import CustomDialogLoaderComponent from "../../../core/components/common/custom-dialog-loader.component";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import useGatePass from "../../GatePassPlugin/hooks/useGatePass";
import DriverListDropdownComponent from "../../DriverPlugin/common/driver-list-dropdown.component";
import useDriver from "../../DriverPlugin/hooks/useDriver";
import VehicleListDropdownComponent from "../../VehiclePlugin/common/vehicle-list-dropdown.component";
import useVehicle from "../../VehiclePlugin/hooks/useVehicle";
import useContainer from "../../ContainerPlugin/hooks/useContainer";
import ContainerListDropdownComponent from "../../ContainerPlugin/common/container-list-dropdown.component";
import {useLocation} from "react-router-dom";
import {useUser} from "../../UserPlugin/hooks/useUser";


const AddVehicleDialog = (props) => {
    const {open, setOpen, bookingDetails} = props;

    const theme = useTheme();
    const location = useLocation();

    const {isGeneratingBookingGatePass, generateBookingGatePass, generateOrgBookingGatePass} = useGatePass();
    const {isFetchingDriverList, getOrganizationDriverList, orgDriverList} = useDriver();
    const {isFetchingActiveVehicleList, activeVehicleList, getActiveVehicleList} = useVehicle();
    const {isFetchingContainerList, containerList, getListContainer} = useContainer();
    const {listAllUsersForDropdown, isFetchingAllUsersForDropdown, userListForDropdown} = useUser();

    
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedContainer, setSelectedContainer] = useState(null);

    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    const handleDriverChange = (value) => {
        setSelectedDriver(value);
    }

    const handleVehicleChange = (value) => {
        setSelectedVehicle(value);
    }

    const handleContainerChange = (value) => {
        setSelectedContainer(value);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const handleGenerateBookingGatePass = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const payload = {
                vehicle_uid: selectedVehicle?.vehicle_uid,
                driver_uid: selectedDriver?.profile_uid,
                container_uid: selectedContainer?.container_uid,
                booking_uid: bookingDetails?.booking_uid,
                org_uid: organizationId
            };

            generateOrgBookingGatePass(payload, setOpen);

        } else if (!organizationId) {
            const payload = {
                vehicle_uid: selectedVehicle?.vehicle_uid,
                driver_uid: selectedDriver?.profile_uid,
                container_uid: selectedContainer?.container_uid,
                booking_uid: bookingDetails?.booking_uid,
            };

            generateBookingGatePass(payload, setOpen);
        }
    }

    const actions = [
        {
            label: "Cancel",
            onClick: (e) => setOpen(false),
            variant: "outlined",
            customStyles: {
                opacity: 1, mr: 1
            }
        },
        {
            label: "Add",
            onClick: (e) => (selectedVehicle && selectedDriver) ? handleGenerateBookingGatePass(e) : null,
            customStyles: {
                opacity: (selectedVehicle && selectedDriver && !isGeneratingBookingGatePass) ? "100%" : "55%",
                cursor: (selectedVehicle && selectedDriver && !isGeneratingBookingGatePass) ? "pointer" : "not-allowed !important",
                visibility: (isGeneratingBookingGatePass) ? "hidden" : "visible",
            },
        }
    ]

    const handleGetContainerList = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const parameters = {orgUID: organizationId};
            getListContainer(parameters);

        } else {
            getListContainer();
        }
    }

    const globalSx = {
       //
    }


    return (
        <>
            <CustomDialog
                open={open}
                onClose={(isGeneratingBookingGatePass) ? null : handleClose}
                title="Add Vehicle"
                subtitle={"Select a vehicle to add to this booking request"}
                hasDividers={true}
                content={
                    <Box sx={{overflowY: "auto"}}>
                        {(isGeneratingBookingGatePass) ? 
                            <Box sx={{mb: "80px"}}>
                                <CustomDialogLoaderComponent 
                                    icon={<CircularProgress size={40} sx={{color: theme.palette.primary.main}} />} 
                                    message={"Adding vehicle to booking..."}
                                />
                            </Box>  
                        :
                            <>
                                <Box sx={{mb: 3}}>
                                    <CustomTypography 
                                        variant="subtitle2"
                                        text={"Vehicle"}
                                        customStyles={{
                                            marginBottom: 1, 
                                        }}
                                    />
                                    <VehicleListDropdownComponent 
                                        value={selectedVehicle}
                                        onChange={handleVehicleChange}
                                        fullWidth={true}
                                        size={"medium"}
                                        disabled={isGeneratingBookingGatePass}
                                        isFetchingActiveVehicleList={isFetchingActiveVehicleList}
                                        getActiveVehicleList={getActiveVehicleList}
                                        activeVehicleList={activeVehicleList}
                                    />
                                </Box>

                                <Box sx={{mb: 3}}>
                                    <CustomTypography 
                                        variant="subtitle2"
                                        text={"Driver"}
                                        customStyles={{
                                            marginBottom: 1, 
                                            mt: 2
                                        }}
                                    />
                                    <DriverListDropdownComponent 
                                        value={selectedDriver}
                                        onChange={handleDriverChange}
                                        fullWidth={true}
                                        size={"medium"}
                                        disabled={isGeneratingBookingGatePass}
                                        isFetchingAllUsersForDropdown={isFetchingAllUsersForDropdown}
                                        listAllUsersForDropdown={listAllUsersForDropdown}
                                        userListForDropdown={userListForDropdown}
                                        isFetchingDriverList={isFetchingDriverList} 
                                        getOrganizationDriverList={getOrganizationDriverList}
                                        orgDriverList={orgDriverList}
                                    />
                                </Box>

                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Container"}
                                    customStyles={{
                                        marginBottom: 1, 
                                        mt: 2
                                    }}
                                />
                                <ContainerListDropdownComponent 
                                    value={selectedContainer}
                                    onChange={handleContainerChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isGeneratingBookingGatePass}
                                    isFetchingContainerList={isFetchingContainerList}
                                    containerList={containerList}
                                    getListContainer={handleGetContainerList}
                                />
                            </>
                        } 
                    </Box>
                }
                actions={!isGeneratingBookingGatePass && actions}
                maxWidth={"sm"}
                customStyles={{}}
                globalSx={globalSx}
            />
        </>
    )
}

export default AddVehicleDialog
